import {
  useState,
  useEffect,
  useRef,
  useSyncExternalStore,
  useCallback,
} from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useSponsorInfo } from "@/hooks/useSponsorInfo";
import {
  getSponsorAdState,
  subscribeSponsorAd,
  hideSponsorAd,
} from "@/stores/sponsorAdStore";
import { Link } from "react-router-dom";
import { X, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { isPWAInstalled } from "@/utils/pwaUtils";
import { useIsMobile } from "@/hooks/useResponsive";

export function SponsorAdModal() {
  const adState = useSyncExternalStore(subscribeSponsorAd, getSponsorAdState);
  const { isOpen, sponsorId, trackTitle } = adState;
  const [canClose, setCanClose] = useState(false);
  const [animating, setAnimating] = useState(false);
  const sessionRef = useRef(0);
  const isMobile = useIsMobile();
  const isPWA = isPWAInstalled();
  const isFullScreen = isMobile;

  const { data: sponsorData } = useSponsorInfo(
    sponsorId,
    !!sponsorId && isOpen,
  );

  useEffect(() => {
    if (isOpen) {
      const session = ++sessionRef.current;
      setCanClose(false);
      setAnimating(true);
      const timer = setTimeout(() => {
        if (sessionRef.current === session) {
          setCanClose(true);
          hideSponsorAd();
        }
      }, 15000);
      return () => clearTimeout(timer);
    } else {
      setAnimating(false);
    }
  }, [isOpen, sponsorId]);

  // Belt-and-suspenders body scroll lock: Radix's own lock is tied to the
  // Dialog's Presence lifecycle, which can release a beat before framer
  // motion finishes animating the ad off-screen. Driving the lock directly
  // off the store's isOpen keeps the page pinned for the ad's full lifetime.
  // Set/clear fixed values rather than capturing "original" styles, since
  // this can run interleaved with Radix's own lock/unlock on the same
  // element — snapshotting whichever value the other lock left behind would
  // fight over the restore and can leave the page stuck unscrollable.
  useEffect(() => {
    if (!isOpen) return;
    // Compensate for the vertical scrollbar disappearing when the body can no
    // longer scroll — otherwise the page content reflows into that freed-up
    // width the instant the modal opens, reading as a jump/flicker.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (canClose) {
      hideSponsorAd();
    }
  }, [canClose]);

  const sponsorName = sponsorData?.display_name || "Sponsor";
  const sponsorAvatar = sponsorData?.avatar_url;
  const sponsorUsername = sponsorData?.sponsorUsername || sponsorData?.username;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && canClose) hideSponsorAd();
      }}
    >
      <DialogContent
        // Above the docked music player bar (z-[65]) so the ad fully covers
        // it instead of the player showing through/on top of the ad.
        overlayClassName="z-[70]"
        className={
          isFullScreen
            ? "fixed inset-0 z-[70] w-full max-w-none border-0 p-0 overflow-hidden bg-transparent rounded-none translate-x-0 translate-y-0 left-0 top-0 [&>button]:hidden"
            : "z-[70] sm:max-w-md border-0 p-0 overflow-hidden bg-transparent [&>button]:hidden"
        }
        onPointerDownOutside={(e) => {
          if (!canClose) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (!canClose) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (!canClose) e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">Sponsored Content</DialogTitle>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key={sponsorId || "sponsor"}
              initial={{ opacity: 0, scale: isFullScreen ? 1 : 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: isFullScreen ? 1 : 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={
                isFullScreen
                  ? "relative h-full flex flex-col bg-[#17143A] font-archivo"
                  : "relative rounded-[3px] bg-[#17143A] font-archivo"
              }
            >
              {/* AD Badge */}
              <div className="absolute top-3.5 left-3.5 z-10">
                <span className="rounded-[2px] border border-[#E9B949]/45 px-2 py-1.5 font-archivo text-[10px] font-bold uppercase tracking-widest text-[#E9B949]">
                  AD
                </span>
              </div>

              {/* Close button */}
              <button
                onClick={handleClose}
                disabled={!canClose}
                className={`absolute top-3.5 right-3.5 z-10 transition-opacity duration-300 ${
                  canClose
                    ? "text-white cursor-pointer opacity-100"
                    : "text-white cursor-not-allowed opacity-40"
                }`}
              >
                <X className="h-[17px] w-[17px]" strokeWidth={1.7} />
              </button>

              {/* Content */}
              <div
                className={
                  isFullScreen
                    ? "flex flex-col items-center justify-center gap-3.5 px-8 flex-1"
                    : "flex flex-col items-center gap-3.5 px-8 pt-11 pb-[26px]"
                }
              >
                <Avatar
                  className={
                    isFullScreen
                      ? "h-28 w-28 ring-[3px] ring-[#8B6BF0]/40"
                      : "h-[88px] w-[88px] ring-[3px] ring-[#8B6BF0]/40"
                  }
                >
                  <AvatarImage src={sponsorAvatar || ""} alt={sponsorName} />
                  <AvatarFallback className="bg-[#241F52] text-lg font-bold text-white">
                    {sponsorName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="text-center flex flex-col gap-1">
                  <p className="font-archivo text-xs text-[#9A96C4]">
                    Sponsored by
                  </p>
                  <h3
                    className={
                      isFullScreen
                        ? "font-archivo text-2xl font-bold text-white"
                        : "font-archivo text-2xl font-bold text-white"
                    }
                  >
                    {sponsorName}
                  </h3>
                  {trackTitle && (
                    <p
                      className={
                        isFullScreen
                          ? "font-archivo text-base text-[#9A96C4]"
                          : "font-archivo text-sm text-[#9A96C4]"
                      }
                    >
                      Supporting{" "}
                      <span className="font-semibold text-white">
                        "{trackTitle}"
                      </span>
                    </p>
                  )}
                </div>

                {sponsorUsername && (
                  <Link
                    to={`/profiles/${sponsorUsername}`}
                    onClick={() => hideSponsorAd()}
                    className={
                      isFullScreen
                        ? "mt-1 inline-flex items-center gap-2 rounded-[2px] bg-white px-[22px] py-3.5 font-archivo text-base font-semibold text-[#1E1B4B]"
                        : "mt-1 inline-flex items-center gap-2 rounded-[2px] bg-white px-[22px] py-3.5 font-archivo text-sm font-semibold text-[#1E1B4B]"
                    }
                  >
                    Visit profile
                    <ExternalLink
                      className={isFullScreen ? "h-4 w-4" : "h-[15px] w-[15px]"}
                      strokeWidth={1.7}
                    />
                  </Link>
                )}

                <p className="mt-1 font-archivo text-xs text-[#8B88A3]">
                  {canClose
                    ? "You can now close this"
                    : "This ad helps support independent artists"}
                </p>
              </div>

              {/* Progress bar */}
              <div className="h-1 w-full bg-white/[0.14] overflow-hidden">
                <div
                  className={`h-full bg-[#8B6BF0] ${isOpen && animating ? "animate-sponsor-progress" : ""}`}
                  style={{ width: canClose ? "100%" : undefined }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
